import { useRef } from 'react';
import { db } from '../model/db.js';
import { resolvePrayerHashtagIds } from '../sync/PrayerSync.js';
import { useAsyncLiveQuery } from './useAsyncLiveQuery.js';

/**
 * A prayer as the UI renders it - tags resolved to text, no sync
 * bookkeeping.
 */
export interface ClientPrayer {
  id: string;
  text: string;
  isList: boolean;
  completed: boolean;
  targetDate?: string;
  updateDate: string;
  hashtags?: string[];
}

/**
 * Live-reactive lookup of a single prayer by id - the prayer equivalent of
 * `usePassageById` (see its comments for the full rationale). Components
 * render from this rather than a prop snapshot, so background sync writes
 * show up without the page having to reload its list.
 *
 * Returns `undefined` while loading, and when `id` is `undefined` or isn't
 * (or is no longer) a local prayer - see `isLoading` to tell those apart.
 * Like `usePassageById`, it assumes `id` is stable for the hook instance's
 * lifetime - callers key the component on the id.
 */
export const usePrayerById = (id: string | undefined): { prayer: ClientPrayer | undefined; isLoading: boolean } => {
  const { data: localPrayer, isLoading: prayerLoading } = useAsyncLiveQuery(
    () => (id === undefined ? Promise.resolve(undefined) : db.prayers.get(id)),
    [id]
  );

  const hashtagIdsKey = (localPrayer?.hashtagIds ?? []).join(',');

  // stamped with the key it was computed for - see usePassageById for why
  // useLiveQuery's own status can't tell us the tags have caught up
  const { data: resolvedTags } = useAsyncLiveQuery(
    async () => {
      if (hashtagIdsKey.length === 0) return { forKey: hashtagIdsKey, tags: [] as string[] };

      const rows = await resolvePrayerHashtagIds(hashtagIdsKey.split(','));
      return { forKey: hashtagIdsKey, tags: rows.map((row) => row.tag) };
    },
    [hashtagIdsKey],
    { forKey: '', tags: [] }
  );

  const tagsAreFresh = resolvedTags.forKey === hashtagIdsKey;

  // only the very first resolution waits for the tags to catch up - after
  // that, a tag change from a background sync shouldn't flip an open card
  // back to "loading" (and unmount it, losing any in-progress edit)
  const hasResolvedRef = useRef(false);
  const stillResolvingFirstLoad = !tagsAreFresh && !hasResolvedRef.current;
  if (localPrayer && tagsAreFresh) {
    hasResolvedRef.current = true;
  }

  if (id === undefined) return { prayer: undefined, isLoading: false };
  if (prayerLoading || (localPrayer && stillResolvingFirstLoad)) return { prayer: undefined, isLoading: true };
  // a row awaiting its pushed delete is already gone as far as the UI cares
  if (!localPrayer || localPrayer.syncStatus === 'pendingDelete') return { prayer: undefined, isLoading: false };

  return {
    prayer: {
      id: localPrayer.id,
      text: localPrayer.text,
      isList: localPrayer.isList,
      completed: localPrayer.completed,
      targetDate: localPrayer.targetDate,
      updateDate: localPrayer.updateDate,
      hashtags: resolvedTags.tags.length > 0 ? resolvedTags.tags : undefined,
    },
    isLoading: false,
  };
};
